import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, orderBy, getDocs, limit,
  doc, setDoc, deleteDoc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../../firebase';
import {
  Plus, Pencil, Trash2, Copy, RefreshCw, Loader2,
  CheckSquare, Square, ChevronUp, ChevronDown, X,
  AlertTriangle, CheckCircle2, ChevronRight, Zap, Download,
} from 'lucide-react';

// ─── helpers ──────────────────────────────────────────────────────────────────

function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function slugify(value = '') {
  return String(value).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function formatCLP(value) {
  const n = Number(value);
  if (!n || n <= 0) return null; // null = indica problema
  return `$${n.toLocaleString('es-CL')}`;
}

const FUEL_KEYS = ['bencina93', 'bencina95', 'bencina97', 'diesel'];
const FUEL_LABELS = { bencina93: '93', bencina95: '95', bencina97: '97', diesel: 'Diésel' };

function hasZeros(rec) {
  return FUEL_KEYS.some(k => !(Number(rec[k]) > 0));
}

const ESTADO_CFG = {
  real_cne_rm:              { label: 'CNE',       cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  real_online:              { label: 'real',      cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  base:                     { label: 'base',      cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  aproximado:               { label: 'Aprox.',    cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  pendiente_actualizacion:  { label: 'Pendiente', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_CFG[estado] || { label: estado || '—', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

const DAY_NAMES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

function emptyForm() {
  return {
    fechaCorte: '', semana: '', anio: new Date().getFullYear(),
    pais: 'CL', region: 'Chile', comuna: '',
    bencina93: '', bencina95: '', bencina97: '', diesel: '',
    estado: 'aproximado', fuente: 'manual',
    fuenteNombre: 'Tabla base KawésCar', fuenteUrl: '',
    diaCorte: 'viernes',
  };
}

// Detectar semanas duplicadas (misma fechaCorte + region)
function getDuplicateKeys(records) {
  const count = {};
  for (const r of records) {
    const key = `${r.fechaCorte}|${r.region || 'Chile'}`;
    count[key] = (count[key] || 0) + 1;
  }
  return new Set(Object.keys(count).filter(k => count[k] > 1));
}

// ─── component ────────────────────────────────────────────────────────────────

export default function FuelPricesAdminPanel() {
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [filters, setFilters]       = useState({ anio: new Date().getFullYear(), estado: 'todos' });
  const [sortDir, setSortDir]       = useState('desc');
  const [selected, setSelected]     = useState(new Set());
  const [showModal, setShowModal]   = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);

  async function handleFetchCurrent() {
    setFetching(true);
    setFetchResult(null);
    setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'fetchCurrentFuelPrices');
      const { data } = await fn({});
      setFetchResult(data);
      if (data.ok && !data.skipped) {
        setSuccess(data.mensaje || 'Precios actualizados.');
        await loadRecords();
      } else if (data.skipped) {
        setSuccess(data.mensaje || 'Ya existe un registro real para esta semana.');
      }
    } catch (err) {
      const msg = err?.message || String(err);
      setError('Error al obtener precios: ' + msg);
    } finally {
      setFetching(false);
    }
  }

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = query(
        collection(db, 'precios_combustible_semanal'),
        where('anio', '==', filters.anio),
        orderBy('fechaCorte', sortDir),
      );
      const snap = await getDocs(q);
      let data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (filters.estado !== 'todos') data = data.filter(r => r.estado === filters.estado);
      setRecords(data);
      setSelected(new Set());
    } catch (err) {
      setError('Error al cargar: ' + (err?.message || String(err)));
    } finally {
      setLoading(false);
    }
  }, [filters, sortDir]);

  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => { if (!success) return; const t = setTimeout(() => setSuccess(''), 3500); return () => clearTimeout(t); }, [success]);

  // ── métricas de salud ───────────────────────────────────────────────────────

  const withZeros = records.filter(hasZeros);
  const duplicateKeys = getDuplicateKeys(records);
  const withDups = records.filter(r => duplicateKeys.has(`${r.fechaCorte}|${r.region || 'Chile'}`));
  const issues = withZeros.length + withDups.length;

  // ── form helpers ────────────────────────────────────────────────────────────

  function handleDateChange(dateStr) {
    if (!dateStr) { setForm(f => ({ ...f, fechaCorte: '' })); return; }
    const d = new Date(`${dateStr}T12:00:00`);
    setForm(f => ({
      ...f,
      fechaCorte: dateStr,
      semana: getISOWeek(d),
      anio: d.getFullYear(),
      diaCorte: DAY_NAMES[d.getDay()] || 'viernes',
    }));
  }

  // Autocompletar precios desde el registro más reciente anterior a la fecha actual
  async function handleCopyFromPrev() {
    const refDate = form.fechaCorte || new Date().toISOString().slice(0, 10);
    setLoadingPrev(true);
    try {
      const q = query(
        collection(db, 'precios_combustible_semanal'),
        where('fechaCorte', '<', refDate),
        where('region', '==', form.region || 'Chile'),
        orderBy('fechaCorte', 'desc'),
        limit(1),
      );
      const snap = await getDocs(q);
      if (snap.empty) {
        setError('No se encontró registro anterior para copiar precios.');
        return;
      }
      const prev = snap.docs[0].data();
      setForm(f => ({
        ...f,
        bencina93: Number(prev.bencina93) > 0 ? prev.bencina93 : f.bencina93,
        bencina95: Number(prev.bencina95) > 0 ? prev.bencina95 : f.bencina95,
        bencina97: Number(prev.bencina97) > 0 ? prev.bencina97 : f.bencina97,
        diesel:    Number(prev.diesel)    > 0 ? prev.diesel    : f.diesel,
        estado: f.estado === 'real_online' ? 'aproximado' : f.estado,
      }));
      setFormErrors({});
    } catch (err) {
      setError('Error al buscar registro anterior: ' + (err?.message || String(err)));
    } finally {
      setLoadingPrev(false);
    }
  }

  function validateForm() {
    const errs = {};
    if (!form.fechaCorte) errs.fechaCorte = 'Obligatoria';
    if (!(Number(form.bencina93) > 0)) errs.bencina93 = '> 0';
    if (!(Number(form.bencina95) > 0)) errs.bencina95 = '> 0';
    if (!(Number(form.bencina97) > 0)) errs.bencina97 = '> 0';
    if (!(Number(form.diesel)    > 0)) errs.diesel    = '> 0';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function openNew() {
    setEditId(null); setForm(emptyForm()); setFormErrors({}); setShowAdvanced(false); setShowModal(true);
  }

  function openEdit(rec) {
    setEditId(rec.id);
    setForm({
      fechaCorte: rec.fechaCorte || '', semana: rec.semana || '',
      anio: rec.anio || new Date().getFullYear(),
      pais: rec.pais || 'CL', region: rec.region || 'Chile', comuna: rec.comuna || '',
      bencina93: rec.bencina93 ?? '', bencina95: rec.bencina95 ?? '',
      bencina97: rec.bencina97 ?? '', diesel: rec.diesel ?? '',
      estado: rec.estado || 'aproximado', fuente: rec.fuente || 'manual',
      fuenteNombre: rec.fuenteNombre || '', fuenteUrl: rec.fuenteUrl || '',
      diaCorte: rec.diaCorte || 'viernes',
    });
    setFormErrors({}); setShowAdvanced(false); setShowModal(true);
  }

  function openDuplicate(rec) {
    const date = new Date(`${rec.fechaCorte}T12:00:00`);
    date.setDate(date.getDate() + 7);
    const nextDate = date.toISOString().slice(0, 10);
    setEditId(null);
    setForm({
      fechaCorte: nextDate, semana: getISOWeek(date), anio: date.getFullYear(),
      pais: rec.pais || 'CL', region: rec.region || 'Chile', comuna: rec.comuna || '',
      bencina93: rec.bencina93 ?? '', bencina95: rec.bencina95 ?? '',
      bencina97: Number(rec.bencina97) > 0 ? rec.bencina97 : (rec.bencina97 ?? ''),
      diesel:    Number(rec.diesel)    > 0 ? rec.diesel    : (rec.diesel    ?? ''),
      estado: 'aproximado', fuente: 'manual',
      fuenteNombre: 'Tabla base KawésCar', fuenteUrl: '',
      diaCorte: DAY_NAMES[date.getDay()] || 'viernes',
    });
    setFormErrors({}); setShowAdvanced(false); setShowModal(true);
  }

  function closeModal() { setShowModal(false); setEditId(null); setFormErrors({}); }

  // ── CRUD ────────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!validateForm()) return;
    setSaving(true); setError('');
    try {
      const isNew = !editId;
      const docId = editId || `${form.fechaCorte}-${slugify(form.region || 'chile')}`;
      await setDoc(doc(db, 'precios_combustible_semanal', docId), {
        anio: Number(form.anio), semana: Number(form.semana),
        fechaCorte: form.fechaCorte, diaCorte: form.diaCorte || 'viernes',
        pais: form.pais || 'CL', region: form.region || 'Chile',
        comuna: form.comuna || null,
        bencina93: Number(form.bencina93), bencina95: Number(form.bencina95),
        bencina97: Number(form.bencina97), diesel: Number(form.diesel),
        estado: form.estado || 'aproximado', fuente: form.fuente || 'manual',
        fuenteNombre: form.fuenteNombre || 'Tabla base KawésCar',
        fuenteUrl: form.fuenteUrl || '',
        actualizadoEn: serverTimestamp(),
        ...(isNew ? { creadoEn: serverTimestamp() } : {}),
      }, { merge: true });
      setSuccess(isNew ? 'Precio creado.' : 'Precio actualizado.');
      closeModal();
      await loadRecords();
    } catch (err) {
      setError('Error al guardar: ' + (err?.message || String(err)));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(ids) {
    setDeleting(true); setError('');
    try {
      if (ids.length === 1) {
        await deleteDoc(doc(db, 'precios_combustible_semanal', ids[0]));
      } else {
        const batch = writeBatch(db);
        ids.forEach(id => batch.delete(doc(db, 'precios_combustible_semanal', id)));
        await batch.commit();
      }
      setSuccess(`${ids.length} registro${ids.length > 1 ? 's' : ''} eliminado${ids.length > 1 ? 's' : ''}.`);
      setConfirmDel(null);
      await loadRecords();
    } catch (err) {
      setError('Error al eliminar: ' + (err?.message || String(err)));
    } finally {
      setDeleting(false);
    }
  }

  // ── selection ───────────────────────────────────────────────────────────────

  const allSel = records.length > 0 && selected.size === records.length;
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(records.map(r => r.id)));
  const toggleOne = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // ── form state helpers ──────────────────────────────────────────────────────

  const formHasZeros = FUEL_KEYS.some(k => !(Number(form[k]) > 0));

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Precios combustible semanal</h2>
          <p className="text-sm text-slate-500">
            {records.length} registros · <span className="font-mono text-xs text-slate-400">precios_combustible_semanal</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.size > 0 && (
            <button
              onClick={() => setConfirmDel({ ids: [...selected], label: `${selected.size} registros` })}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
            >
              <Trash2 className="h-4 w-4" /> Eliminar ({selected.size})
            </button>
          )}
          <button onClick={loadRecords} disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleFetchCurrent}
            disabled={fetching}
            title="Obtener precios actuales desde fuente online (CNE / BencinApp)"
            className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
          >
            {fetching
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Download className="h-4 w-4" />}
            {fetching ? 'Obteniendo...' : 'Obtener precio actual'}
          </button>
          <button onClick={openNew}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Nuevo precio
          </button>
        </div>
      </div>

      {/* Banner de problemas */}
      {!loading && issues > 0 && (
        <div className="flex flex-wrap items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="flex-1 text-sm text-amber-800">
            <span className="font-semibold">Se detectaron {issues} problemas: </span>
            {withZeros.length > 0 && (
              <span>{withZeros.length} registro{withZeros.length > 1 ? 's' : ''} con precios en cero</span>
            )}
            {withZeros.length > 0 && withDups.length > 0 && <span> · </span>}
            {withDups.length > 0 && (
              <span>{withDups.length} registro{withDups.length > 1 ? 's' : ''} con semana duplicada</span>
            )}
            <span className="ml-1 text-amber-600">— edítalos para corregir.</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase text-slate-500">Año</label>
          <select value={filters.anio} onChange={e => setFilters(f => ({ ...f, anio: Number(e.target.value) }))}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase text-slate-500">Estado</label>
          <select value={filters.estado} onChange={e => setFilters(f => ({ ...f, estado: e.target.value }))}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
            <option value="todos">Todos</option>
            <option value="real_cne_rm">CNE</option>
            <option value="real_online">Online</option>
            <option value="base">Base</option>
            <option value="aproximado">Aproximado</option>
            <option value="pendiente_actualizacion">Pendiente</option>
          </select>
        </div>
        <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
          className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-100">
          {sortDir === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          {sortDir === 'desc' ? 'Más recientes' : 'Más antiguos'}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />{error}
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" />{success}
        </div>
      )}

      {/* Resultado del fetch */}
      {fetchResult && !fetchResult.skipped && fetchResult.precios && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Precios obtenidos · {fetchResult.fechaCorte} · S{fetchResult.semana}
            </p>
            <div className="flex items-center gap-1.5">
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${fetchResult.estado === 'real_online' ? 'border-emerald-200 bg-emerald-100 text-emerald-800' : 'border-amber-200 bg-amber-100 text-amber-800'}`}>
                {fetchResult.estado === 'real_online' ? 'real' : 'estimado'}
              </span>
              <span className="text-xs text-slate-400">{fetchResult.fuente}</span>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {[['bencina93','93'], ['bencina95','95'], ['bencina97','97'], ['diesel','Diésel']].map(([k, label]) => (
              <div key={k} className="rounded-lg border border-slate-200 bg-white p-3 text-center">
                <p className="text-[10px] font-bold uppercase text-slate-400">{label}</p>
                <p className={`mt-1 text-lg font-black ${fetchResult.precios[k] > 0 ? 'text-slate-800' : 'text-red-500'}`}>
                  {fetchResult.precios[k] > 0 ? `$${Number(fetchResult.precios[k]).toLocaleString('es-CL')}` : '$0'}
                </p>
              </div>
            ))}
          </div>
          {fetchResult.intentos?.some(i => !i.ok) && (
            <p className="mt-2 text-[11px] text-slate-400">
              Intentos: {fetchResult.intentos.map(i => `${i.nombre} ${i.ok ? '✓' : `✗ (${i.error})`}`).join(' · ')}
            </p>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Cargando precios...
          </div>
        ) : records.length === 0 ? (
          <div className="py-12 text-center text-slate-400">
            <p className="font-medium">No hay precios para este filtro.</p>
            <p className="mt-1 text-sm">Cambia los filtros o agrega un nuevo precio.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="w-8 px-3 py-3">
                  <button onClick={toggleAll}>
                    {allSel ? <CheckSquare className="h-4 w-4 text-blue-600" /> : <Square className="h-4 w-4" />}
                  </button>
                </th>
                <th className="px-3 py-3">Fecha corte</th>
                <th className="px-3 py-3">Sem.</th>
                <th className="px-3 py-3 text-right">93</th>
                <th className="px-3 py-3 text-right">95</th>
                <th className="px-3 py-3 text-right">97</th>
                <th className="px-3 py-3 text-right">Diésel</th>
                <th className="px-3 py-3">Estado</th>
                <th className="px-3 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map(rec => {
                const zeros = hasZeros(rec);
                const isDup = duplicateKeys.has(`${rec.fechaCorte}|${rec.region || 'Chile'}`);
                const rowIssue = zeros || isDup;
                return (
                  <tr key={rec.id} className={`transition-colors ${selected.has(rec.id) ? 'bg-blue-50' : rowIssue ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-slate-50'}`}>
                    <td className="px-3 py-2.5">
                      <button onClick={() => toggleOne(rec.id)}>
                        {selected.has(rec.id)
                          ? <CheckSquare className="h-4 w-4 text-blue-600" />
                          : <Square className="h-4 w-4 text-slate-300" />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-slate-800">{rec.fechaCorte}</span>
                        {isDup && (
                          <span className="rounded border border-orange-200 bg-orange-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-orange-700">dup</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-500">S{rec.semana}</td>
                    {FUEL_KEYS.map(k => {
                      const val = formatCLP(rec[k]);
                      return (
                        <td key={k} className="px-3 py-2.5 text-right">
                          {val
                            ? <span className="font-medium text-slate-700">{val}</span>
                            : <span className="font-semibold text-red-500">$0</span>
                          }
                        </td>
                      );
                    })}
                    <td className="px-3 py-2.5">
                      <EstadoBadge estado={rec.estado} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(rec)} title="Editar"
                          className={`rounded p-1.5 text-slate-500 hover:bg-slate-100 ${zeros ? 'text-amber-600 hover:bg-amber-100' : ''}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => openDuplicate(rec)} title="Duplicar semana siguiente"
                          className="rounded p-1.5 text-slate-500 hover:bg-slate-100">
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setConfirmDel({ ids: [rec.id], label: `el precio del ${rec.fechaCorte}` })}
                          title="Eliminar"
                          className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Form Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl">
            {/* Modal header */}
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
              <h3 className="text-base font-bold text-slate-900">
                {editId ? 'Editar precio semanal' : 'Nuevo precio semanal'}
              </h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5 p-6">

              {/* Alerta de ceros en el formulario */}
              {formHasZeros && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div className="flex-1 text-sm text-amber-800">
                    <span className="font-semibold">Hay precios en cero.</span>
                    {' '}Completa todos los precios o usa los del registro anterior.
                  </div>
                  <button
                    onClick={handleCopyFromPrev}
                    disabled={!form.fechaCorte || loadingPrev}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    {loadingPrev ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                    Copiar anterior
                  </button>
                </div>
              )}

              {/* Fecha */}
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Fecha de corte *</label>
                <div className="flex gap-2">
                  <input type="date" value={form.fechaCorte} onChange={e => handleDateChange(e.target.value)}
                    className={`flex-1 ${inputCls(formErrors.fechaCorte)}`} />
                  {form.semana && (
                    <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-mono text-slate-500">
                      S{form.semana}
                    </div>
                  )}
                </div>
                {formErrors.fechaCorte && <p className="mt-1 text-xs text-red-600">{formErrors.fechaCorte}</p>}
              </div>

              {/* Precios — sección principal */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Precios ($/L) *</label>
                  {!formHasZeros && form.fechaCorte && (
                    <button onClick={handleCopyFromPrev} disabled={loadingPrev}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50">
                      {loadingPrev ? <Loader2 className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
                      Copiar anterior
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {FUEL_KEYS.map(k => (
                    <div key={k}>
                      <label className="mb-1 block text-xs font-medium text-slate-500">
                        Bencina {FUEL_LABELS[k] === 'Diésel' ? '' : FUEL_LABELS[k]}{FUEL_LABELS[k] === 'Diésel' ? 'Diésel' : ''}
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                        <input
                          type="number"
                          value={form[k]}
                          onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                          placeholder="0"
                          className={`w-full rounded-lg border py-2 pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                            formErrors[k] ? 'border-red-400 bg-red-50' : Number(form[k]) > 0 ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'
                          }`}
                        />
                      </div>
                      {formErrors[k] && <p className="mt-0.5 text-xs text-red-600">{formErrors[k]}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Estado */}
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Estado de los precios</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'real_cne_rm',  label: 'Real CNE',      desc: 'Fuente oficial CNE' },
                    { value: 'real_online',  label: 'Real online',   desc: 'Obtenido de sitio web' },
                    { value: 'aproximado',   label: 'Aproximado',    desc: 'Basado en semana anterior' },
                    { value: 'base',         label: 'Base',          desc: 'Valores de referencia' },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setForm(f => ({ ...f, estado: opt.value }))}
                      className={`rounded-xl border p-3 text-left transition-all ${
                        form.estado === opt.value
                          ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-300'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="text-xs font-semibold text-slate-800">{opt.label}</div>
                      <div className="mt-0.5 text-[10px] text-slate-400">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Más opciones (colapsable) */}
              <div>
                <button type="button"
                  onClick={() => setShowAdvanced(v => !v)}
                  className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700">
                  <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
                  Más opciones (región, fuente, día corte)
                </button>
                {showAdvanced && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Región</label>
                      <input type="text" value={form.region} placeholder="Chile"
                        onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                        className={inputCls()} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Fuente</label>
                      <select value={form.fuente} onChange={e => setForm(f => ({ ...f, fuente: e.target.value }))}
                        className={inputCls()}>
                        <option value="manual">Manual</option>
                        <option value="CNE">CNE</option>
                        <option value="ENAP">ENAP</option>
                        <option value="estimacion_kawescar">Estimación KawésCar</option>
                        <option value="online">Online</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Nombre fuente</label>
                      <input type="text" value={form.fuenteNombre}
                        onChange={e => setForm(f => ({ ...f, fuenteNombre: e.target.value }))}
                        className={inputCls()} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">URL fuente</label>
                      <input type="url" value={form.fuenteUrl} placeholder="https://..."
                        onChange={e => setForm(f => ({ ...f, fuenteUrl: e.target.value }))}
                        className={inputCls()} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">Día corte</label>
                      <select value={form.diaCorte} onChange={e => setForm(f => ({ ...f, diaCorte: e.target.value }))}
                        className={inputCls()}>
                        {DAY_NAMES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">País</label>
                      <input type="text" value={form.pais}
                        onChange={e => setForm(f => ({ ...f, pais: e.target.value }))}
                        className={inputCls()} />
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" />{error}
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
                <button onClick={closeModal}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? 'Guardando...' : 'Guardar precio'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="font-bold text-slate-900">Confirmar eliminación</h3>
            <p className="mt-2 text-sm text-slate-600">
              {confirmDel.ids.length === 1
                ? `¿Eliminar ${confirmDel.label}? Esta acción no se puede deshacer.`
                : `Eliminarás ${confirmDel.ids.length} registros. Esta acción no se puede deshacer.`}
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setConfirmDel(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                Cancelar
              </button>
              <button onClick={() => handleDelete(confirmDel.ids)} disabled={deleting}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function inputCls(error) {
  return `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 ${error ? 'border-red-400 bg-red-50' : 'border-slate-200'}`;
}
